import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, ArrowRight, ExternalLink, BookOpen, DollarSign, FileText,
  CheckCircle2, Users, Search, Calendar, Mic, Award, Heart,
  ChevronRight, Lightbulb, TrendingUp, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const FREE_RESOURCES = [
  {
    category: "College Research",
    icon: Search,
    items: [
      { name: "Niche.com", url: "https://www.niche.com/colleges/", desc: "School grades, student reviews, acceptance rates, net prices, and 'Will you get in?' calculator. The best free college search tool.", highlight: true },
      { name: "CollegeBoard BigFuture", url: "https://bigfuture.collegeboard.org/", desc: "Official College Board tool for finding schools, comparing costs, and understanding financial aid." },
      { name: "College Scorecard", url: "https://collegescorecard.ed.gov/", desc: "U.S. Department of Education data on graduation rates, earnings after graduation, and costs." },
      { name: "Our Career Insights Tool", url: "/career-insights", desc: "Free AI-powered career research — salary, growth, skills, and education requirements for any field.", internal: true },
      { name: "Compare Careers", url: "/career-compare", desc: "Compare 2-3 careers side by side — salary, growth, education. Helps you pick a direction before choosing schools.", internal: true },
    ]
  },
  {
    category: "Test Prep (Free)",
    icon: BookOpen,
    items: [
      { name: "Khan Academy SAT Prep", url: "https://www.khanacademy.org/digital-sat", desc: "Official, free SAT preparation partnered with College Board. Personalized practice, full-length tests, video lessons. This is genuinely excellent.", highlight: true },
      { name: "ACT Free Resources", url: "https://www.act.org/content/act/en/products-and-services/the-act/test-preparation.html", desc: "Official ACT practice tests and study guides directly from the test makers." },
      { name: "LSAT Prep (Khan Academy)", url: "https://www.khanacademy.org/prep/lsat", desc: "Free official LSAT preparation for law school applicants." },
    ]
  },
  {
    category: "Financial Aid & Scholarships",
    icon: DollarSign,
    items: [
      { name: "FAFSA", url: "https://studentaid.gov/h/apply-for-aid/fafsa", desc: "File this first. Federal Student Aid application — the starting point for all financial aid. Opens October 1 every year.", highlight: true },
      { name: "CSS Profile", url: "https://cssprofile.collegeboard.org/", desc: "Required by 400+ private colleges for institutional financial aid. Different from FAFSA." },
      { name: "Fastweb Scholarships", url: "https://www.fastweb.com/", desc: "Largest free scholarship search engine. Match to 1.5 million scholarships." },
      { name: "Scholarship.com", url: "https://www.scholarships.com/", desc: "Another major free scholarship database with personalized matching." },
      { name: "Net Price Calculators", url: "https://collegecost.ed.gov/net-price", desc: "Every college is required to have one. Use it to estimate your actual cost before applying." },
    ]
  },
  {
    category: "Application Help",
    icon: FileText,
    items: [
      { name: "Common App", url: "https://www.commonapp.org/", desc: "The application platform used by 1,000+ colleges. Create your account for free — explore prompts and requirements.", highlight: true },
      { name: "Coalition Application", url: "https://www.coalitionforcollegeaccess.org/", desc: "An alternative to Common App, used by 150+ schools. Free to use." },
      { name: "College Essay Guy (Free Resources)", url: "https://www.collegeessayguy.com/", desc: "Free essay guides, brainstorming exercises, and example essays organized by school." },
      { name: "Johns Hopkins 'Essays That Worked'", url: "https://apply.jhu.edu/essays-that-worked/", desc: "Real admitted student essays from Johns Hopkins — excellent for understanding what works." },
      { name: "Our College Readiness Guide", url: "/college-readiness", desc: "Grade-by-grade timeline from Freshman to Senior year. Essay writing tips. All free.", internal: true },
    ]
  },
  {
    category: "School Counselor Alternatives",
    icon: Users,
    items: [
      { name: "QuestBridge", url: "https://www.questbridge.org/", desc: "Connects high-achieving, low-income students with full scholarships to top colleges. Free program — genuinely life-changing.", highlight: true },
      { name: "College Advising Corps", url: "https://advisingcorps.org/", desc: "Places recent college graduates as full-time advisors in under-resourced high schools. Free advising." },
      { name: "Let's Get Ready", url: "https://letsgetready.org/", desc: "Free SAT prep and college counseling for underserved students." },
      { name: "ScholarMatch", url: "https://scholarmatch.org/", desc: "Free college advising for low-income students. Real human advisors." },
      { name: "NACAC Free Virtual College Fairs", url: "https://www.nacacfairs.org/", desc: "Free virtual college fairs where you can chat directly with admissions officers." },
    ]
  },
  {
    category: "Career & Major Guidance",
    icon: TrendingUp,
    items: [
      { name: "Bureau of Labor Statistics", url: "https://www.bls.gov/ooh/", desc: "The Occupational Outlook Handbook — the most authoritative free source for career data (salary, growth, education requirements).", highlight: true },
      { name: "O*NET OnLine", url: "https://www.onetonline.org/", desc: "Detailed career profiles, skills, work activities, and education requirements for 1,000+ occupations." },
      { name: "Our Career Portal", url: "/career-portal", desc: "Free career guides for Media, Education, Sports, Law School, Medical School, and Graduate School.", internal: true },
    ]
  },
];

const NICHE_HIGHLIGHTS = [
  { school: "MIT", rank: "#1", grade: "A+", rate: "5%", net: "$20,996", sat: "1520-1580" },
  { school: "Yale", rank: "#2", grade: "A+", rate: "4%", net: "$22,408", sat: "1470-1570" },
  { school: "Stanford", rank: "#3", grade: "A+", rate: "4%", net: "$25,078", sat: "1510-1580" },
  { school: "Harvard", rank: "#4", grade: "A+", rate: "4%", net: "$17,525", sat: "1510-1580" },
  { school: "Princeton", rank: "#8", grade: "A+", rate: "5%", net: "$15,313", sat: "1510-1580" },
  { school: "Caltech", rank: "#9", grade: "A+", rate: "3%", net: "$14,513", sat: "1530-1580" },
];

export default function CantAffordPage() {
  return (
    <div className="min-h-screen bg-white pt-16" data-testid="cant-afford-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            "I Can't Afford<br />Ivy League Consulting"
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed mb-3">
            We hear you. And we respect that.
          </p>
          <p className="text-sm text-white/50 max-w-lg mx-auto leading-relaxed">
            Getting into a great college shouldn't depend on your family's income. Below is every free resource we know of — the same ones our advisors recommend to clients — organized so you can build a strong application on your own.
          </p>
        </div>
      </section>

      {/* The honest pitch */}
      <section className="max-w-3xl mx-auto px-4 py-10">
        <Card className="border-[#D4AF37]/20 bg-[#D4AF37]/[0.02]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Heart className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-sm font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>A note from our team</h2>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Our portal costs $99/year because building and maintaining AI tools, maintaining school databases, and providing real consulting support has real costs. But we also know that $99 isn't nothing — especially for families already stressed about paying for college itself.
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                  So here's the deal: if you can't afford our platform right now, <strong>use everything below</strong>. These are genuinely good resources. Some are better than what most paid services offer. If your situation changes, we'll be here. And if you just need our portal for one month to finalize applications, that works too.
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                  <strong>Everyone deserves a fair shot at their dream school.</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Quick stats from Niche */}
      <section className="max-w-4xl mx-auto px-4 pb-10">
        <div className="text-center mb-6">
          <Badge className="bg-[#0F392B]/5 text-[#0F392B] border-[#0F392B]/10 text-xs mb-2">From Niche.com 2026 Rankings</Badge>
          <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Know What You're Aiming For</h2>
          <p className="text-xs text-muted-foreground mt-1">Net price is what families actually pay after financial aid — often far less than sticker price</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="niche-stats">
          {NICHE_HIGHLIGHTS.map((s, i) => (
            <Card key={i} className="border-[#E2E2E2]">
              <CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground">{s.rank} in USA</p>
                <p className="text-sm font-semibold mt-0.5">{s.school}</p>
                <div className="mt-2 space-y-0.5 text-[10px] text-muted-foreground">
                  <p>Accept: <strong className="text-foreground">{s.rate}</strong></p>
                  <p>Net price: <strong className="text-emerald-600">{s.net}</strong></p>
                  <p>SAT: {s.sat}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-center text-[10px] text-muted-foreground mt-3">
          Harvard's net price is $17,525/yr. Princeton is $15,313/yr. Top schools often cost less than state schools for low-income families.
          <a href="https://www.niche.com/colleges/search/best-colleges/" target="_blank" rel="noopener noreferrer" className="text-[#0F392B] hover:underline ml-1">
            See full Niche rankings <ExternalLink className="inline h-2.5 w-2.5" />
          </a>
        </p>
      </section>

      {/* Free Resources */}
      <section className="bg-[#F9F9F7] py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Every Free Resource You Need
            </h2>
            <p className="text-xs text-muted-foreground mt-1">Curated by our admissions advisors. No affiliate links. No hidden agendas.</p>
          </div>

          <div className="space-y-6">
            {FREE_RESOURCES.map((section, si) => (
              <div key={si} data-testid={`resource-section-${si}`}>
                <div className="flex items-center gap-2 mb-3">
                  <section.icon className="h-4 w-4 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold">{section.category}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {section.items.map((item, ii) => (
                    <a key={ii} href={item.internal ? undefined : item.url} target={item.internal ? undefined : "_blank"} rel="noopener noreferrer"
                      className="block" onClick={item.internal ? (e) => { e.preventDefault(); window.location.href = item.url; } : undefined}>
                      <Card className={`border-[#E2E2E2] hover:border-[#0F392B]/20 transition-colors h-full ${item.highlight ? 'ring-1 ring-[#D4AF37]/30' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold">{item.name}</span>
                                {item.highlight && <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20 text-[8px]">Top Pick</Badge>}
                                {item.internal && <Badge variant="outline" className="text-[8px]">Ours — Free</Badge>}
                              </div>
                              <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">{item.desc}</p>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5 ml-2" />
                          </div>
                        </CardContent>
                      </Card>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIY Checklist */}
      <section className="max-w-3xl mx-auto px-4 py-12">
        <h2 className="text-lg font-semibold text-center mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
          The DIY Application Checklist
        </h2>
        <Card className="border-[#E2E2E2]">
          <CardContent className="p-6">
            <p className="text-xs text-muted-foreground mb-4">Follow this timeline and you'll cover 90% of what our paid clients do. We're serious — this list is real.</p>
            <div className="space-y-6">
              {[
                { phase: "Freshman–Sophomore", items: [
                  "Take the most challenging courses available to you (AP, IB, Honors)",
                  "Start extracurriculars — depth > breadth (pick 2-3, lead in at least 1)",
                  "Read widely to build vocabulary for SAT/ACT",
                  "Begin community service that you genuinely care about",
                  "Start a file: save awards, certificates, notable experiences",
                ]},
                { phase: "Junior Year", items: [
                  "Take PSAT in fall → use Khan Academy SAT prep (free, official, excellent)",
                  "Take SAT/ACT by spring — retake in summer if needed",
                  "Build your college list: 3-4 reach, 3-4 target, 2-3 likely",
                  "Use Niche.com for fit scores, net prices, and student reviews",
                  "Start Common App account — review all 7 essay prompts",
                  "Visit campuses (or take virtual tours — most schools offer them free)",
                  "Research financial aid: run Net Price Calculators for your top 10 schools",
                ]},
                { phase: "Senior Fall", items: [
                  "File FAFSA on October 1 (first-come, first-served for some aid)",
                  "File CSS Profile if required by your schools",
                  "Write personal statement (start in August, finalize by October)",
                  "Request recommendation letters (give teachers 4+ weeks)",
                  "Submit Early Action/Early Decision apps by November 1",
                  "Apply for every scholarship you can find (Fastweb, Scholarships.com)",
                ]},
                { phase: "Senior Spring", items: [
                  "Submit Regular Decision apps by January 1-15",
                  "Compare financial aid offers carefully — negotiate if you have leverage",
                  "Attend admitted student events",
                  "Commit by May 1 (National Candidates Reply Date)",
                ]},
              ].map((phase, pi) => (
                <div key={pi}>
                  <h3 className="text-xs font-semibold text-[#0F392B] uppercase tracking-wide mb-2">{phase.phase}</h3>
                  <div className="space-y-1.5">
                    {phase.items.map((item, ii) => (
                      <div key={ii} className="flex items-start gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Soft CTA */}
      <section className="bg-[#0F392B] text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            If Your Situation Changes
          </h2>
          <p className="text-sm text-white/60 max-w-lg mx-auto mb-6">
            Our portal is $99/year with a 7-day free trial. It includes everything above plus AI essay coaching, activity optimization, interview prep, deadline tracking, and a submission-ready checklist. Some families use it just for the final month of application season.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sign-up">
              <Button className="bg-white text-[#0F392B] hover:bg-white/90 h-10 px-6 text-sm font-semibold">
                Try Free for 7 Days <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/portal">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-10 px-6 text-sm">
                See What's Included
              </Button>
            </Link>
          </div>
          <p className="text-[10px] text-white/30 mt-4">No pressure. Genuinely. Use the free resources above first.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">School data from Niche.com 2026 rankings. All external links are free, third-party resources.</p>
        </div>
      </footer>
    </div>
  );
}
